"use client"

// Consent-gated page-view + time-on-page tracking. Only ever sends data if
// the visitor has accepted cookies (see components/cookie-consent.tsx) —
// declining means this module does nothing at all. Session id is a random
// client-generated value kept in sessionStorage (cleared when the tab
// closes), not a fingerprint and not tied to identity beyond the optional
// signed-in user_id the API route attaches server-side.

import { hasAnalyticsConsentSync, hasPersonalizedAdsConsentSync } from "@/components/cookie-consent"

const SESSION_KEY = "goonru_analytics_session"

function getSessionId(): string {
  if (typeof window === "undefined") return ""
  let id = sessionStorage.getItem(SESSION_KEY)
  if (!id) {
    id = crypto.randomUUID()
    sessionStorage.setItem(SESSION_KEY, id)
  }
  return id
}

let currentPath: string | null = null
let viewStartedAt = 0

function sendBeacon(path: string, durationSeconds: number) {
  if (!hasAnalyticsConsentSync()) return
  const payload = JSON.stringify({
    sessionId: getSessionId(),
    path,
    durationSeconds,
    referrer: document.referrer || null,
  })
  const sent = navigator.sendBeacon?.("/api/analytics/event", new Blob([payload], { type: "application/json" }))
  if (!sent) {
    // sendBeacon isn't available/failed (older browser, payload too big) —
    // fall back to a fire-and-forget fetch with keepalive so it still goes
    // out during page unload.
    fetch("/api/analytics/event", { method: "POST", body: payload, headers: { "Content-Type": "application/json" }, keepalive: true }).catch(() => {})
  }
}

/** Call whenever the route changes (including the very first page load).
 * Closes out the previous page's dwell time and starts timing the new one. */
export function trackPageView(path: string) {
  if (typeof window === "undefined" || !hasAnalyticsConsentSync()) return
  const now = Date.now()
  if (currentPath) {
    sendBeacon(currentPath, Math.round((now - viewStartedAt) / 1000))
  }
  currentPath = path
  viewStartedAt = now
}

/** Call once, as early as possible — flushes the in-progress page view when
 * the tab is hidden/closed so short visits still get counted. Idempotent. */
let installed = false
export function installAnalyticsUnloadFlush() {
  if (installed || typeof window === "undefined") return
  installed = true

  const flush = () => {
    if (!currentPath) return
    const now = Date.now()
    sendBeacon(currentPath, Math.round((now - viewStartedAt) / 1000))
    viewStartedAt = now // keep timing if the tab comes back (visibilitychange fires on both hide and show)
  }

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush()
  })
  window.addEventListener("pagehide", flush)
}

let tagAffinityTimer: ReturnType<typeof setTimeout> | null = null
let pendingTags = new Set<string>()

/** Records that the user viewed content with these tags, for opt-in
 * personalized ad targeting. No-op unless personalized-ads consent is on and
 * the user is signed in (the API route itself also requires auth). Calls are
 * debounced/batched so rapid prev/next navigation through a post's detail
 * view doesn't fire a request per click. */
export function trackTagAffinity(tags: string[]) {
  if (typeof window === "undefined" || !hasPersonalizedAdsConsentSync()) return
  if (!tags || tags.length === 0) return

  for (const t of tags) pendingTags.add(t)

  if (tagAffinityTimer) clearTimeout(tagAffinityTimer)
  tagAffinityTimer = setTimeout(() => {
    const batch = Array.from(pendingTags)
    pendingTags = new Set()
    tagAffinityTimer = null
    fetch("/api/analytics/tag-affinity", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tags: batch }),
    }).catch(() => {})
  }, 1500)
}
